import { ADDONFEATURE, AddonFeatureType } from 'core/agency/AddonFeature';
import { AddonFeatureManager } from 'core/auth/AddonFeatureManager';
import { Permission } from 'core/auth/Permission';
import { BidStrategy } from 'core/l2Object/L2Object';
import { PermissionItem } from 'core/permission/PermissionAware';
import {
  addOnEnabled,
  hasFuncs,
  isPmax3Order,
  notArchivedChannel
} from 'core/permission/PermissionDSL';
import { RtbObjective } from 'core/rtbCampaignGroup/RtbCampaignGroup';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import {
  L1ObjectCreateRequest,
  L1ObjectUpdateRequest,
  L1ObjectWebService,
  RestfulL1ObjectWebService
} from 'ws/L1ObjectWebService';
import { L1ObjectObjective } from './L1Object';
import { get } from 'lodash';
import { DeliveryTargetConfig } from 'core/googleMaps/MapTypes';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import {
  getBrandIconUrl,
  getBrandName
} from 'core/channelGroup/channelGroupBrand';

export interface L1ObjectManager {
  createL1Object(l1Object: L1ObjectCreateRequest): Promise<number>;
  updateL1Object(
    l1ObjectId: number | string,
    l1Object: L1ObjectUpdateRequest
  ): Promise<void>;
  getL1Object(l1ObjectId: number | string): Promise<any>;
  getL1Objects(orderId: number | string): Promise<Array<any>>;
  deleteL1Object(l1ObjectId: number): Promise<void>;
  updateL1ObjectState(
    l1ObjectIds: (number | string)[],
    state: 'activate' | 'deactivate'
  ): Promise<void>;
  getL1ObjectOptions(): Promise<SelectOptions[]>;
  getOrderNumber(l1ObjectId: string | number): Promise<string>;
  getL1ObjectObjectiveOptions(channel: string): SelectOptions[];
  getL1ObjectBidStrategyOptions(channel?: string): SelectOptions[];
  isCboBidCapOnlyChannel(channel?: string): boolean;
  getDefaultCboBidStrategy(channel?: string): BidStrategy;
  getNativeObjectiveValue(
    channel: string,
    pmax3Objective?: string
  ): RtbObjective | undefined;
  getAllowedChannels(addonFeatureManager: AddonFeatureManager): string[];
  isChannelAllowed(
    channel: string,
    addonFeatureManager: AddonFeatureManager
  ): boolean;
  getModifyPermissionItem(l1Object?: {
    channel: string;
    adsOrderId: number;
    channelGroupId?: number;
  }): PermissionItem;
  isOutdoorChannel(channel: string): boolean;
  isChannelSupportOutdoorTA(channel: string): boolean;
  getDeliveryTargetConfig(
    channelGroupId?: number,
    language?: string
  ): DeliveryTargetConfig;
}

export class DefaultL1ObjectManager implements L1ObjectManager {
  webService: L1ObjectWebService;

  constructor(
    webService: L1ObjectWebService = new RestfulL1ObjectWebService()
  ) {
    this.webService = webService;
  }

  async createL1Object(l1Object: L1ObjectCreateRequest): Promise<number> {
    return this.webService.createL1Object(l1Object);
  }
  async updateL1Object(
    l1ObjectId: number | string,
    l1Object: L1ObjectUpdateRequest
  ): Promise<void> {
    return this.webService.updateL1Object(l1ObjectId, l1Object);
  }
  async getL1Object(l1ObjectId: number | string): Promise<any> {
    return this.webService.getL1Object(l1ObjectId);
  }
  async getL1Objects(orderId: number | string): Promise<any> {
    return this.webService.getL1Objects(orderId);
  }
  async deleteL1Object(l1ObjectId: number): Promise<void> {
    return this.webService.deleteL1Object(l1ObjectId);
  }
  async updateL1ObjectState(
    l1ObjectIds: (number | string)[],
    state: 'activate' | 'deactivate'
  ): Promise<void> {
    await this.webService.updateL1ObjectState(l1ObjectIds, state);
  }
  async getL1ObjectOptions(): Promise<SelectOptions[]> {
    return this.webService.getL1ObjectOptions();
  }
  async getOrderNumber(l1ObjectId: string | number): Promise<string> {
    return this.webService.getOrderNumber(l1ObjectId);
  }

  getL1ObjectObjectiveOptions(channel: string): SelectOptions[] {
    const channelGroupSetting = ChannelGroupSetting.fromKey(channel);
    const ignoreObjectives = Object.values(L1ObjectObjective).filter(
      (objective) => !channelGroupSetting.config.objectives.includes(objective)
    );
    return createSelectOptionsFromEnum(
      L1ObjectObjective,
      'l1Object.labels.objective.',
      [...ignoreObjectives]
    );
  }

  /**
   * 這個渠道的活動群組是否只能用「出價上限」。
   *
   * 出價上限是 outdoor 活動表單早就有的約束（`onlyBidCap`），但 CBO 開著時活動的出價
   * 策略直接吃群組的值，那條約束會被繞過，而活動層的欄位在 CBO 下又是唯讀的——選錯就
   * 救不回來。所以群組層要收斂到同一個答案。
   *
   * 下拉選項、建立時的預設、編輯時的預設三個呼叫端共用這一支，不各寫一份判斷。
   */
  isCboBidCapOnlyChannel(channel?: string): boolean {
    if (!channel) {
      return false;
    }
    return this.isOutdoorChannel(channel);
  }

  getDefaultCboBidStrategy(channel?: string): BidStrategy {
    return this.isCboBidCapOnlyChannel(channel)
      ? BidStrategy.LOWEST_COST_WITH_BID_CAP
      : BidStrategy.LOWEST_COST_WITHOUT_CAP;
  }

  getL1ObjectBidStrategyOptions(channel?: string): SelectOptions[] {
    const ignoreBidStrategies = this.isCboBidCapOnlyChannel(channel)
      ? [BidStrategy.LOWEST_COST_WITHOUT_CAP]
      : [];
    return createSelectOptionsFromEnum(
      BidStrategy,
      'l1Object.labels.bidStrategy.',
      ignoreBidStrategies
    );
  }

  getNativeObjectiveValue(channel: string, pmax3Objective?: string) {
    if (!pmax3Objective) {
      return;
    }
    const defaultMap = {
      [L1ObjectObjective.AWARENESS]: RtbObjective.AWARENESS,
      [L1ObjectObjective.TRAFFIC]: RtbObjective.TRAFFIC,
      [L1ObjectObjective.SALES]: RtbObjective.SALES
    };
    return defaultMap[pmax3Objective];
  }

  // ECOPD-5566：停用渠道不算「允許的渠道」——
  // 它同時決定建立表單的預設渠道與 isChannelAllowed。
  getAllowedChannels(addonFeatureManager: AddonFeatureManager): string[] {
    return ChannelGroupSetting.activeValues()
      .filter((setting: ChannelGroupSetting) => {
        const featureName = setting.config.addonName;
        if (!featureName) {
          return true;
        }
        return addonFeatureManager.isFeatureEnable(
          featureName as AddonFeatureType
        );
      })
      .map((setting) => setting.key);
  }

  isChannelAllowed(
    channel: string,
    addonFeatureManager: AddonFeatureManager
  ): boolean {
    const allowedChannels = this.getAllowedChannels(addonFeatureManager);
    return allowedChannels.includes(channel);
  }

  getModifyPermissionItem(l1Object?: {
    channel: string;
    adsOrderId: number;
    channelGroupId?: number;
  }): PermissionItem {
    const channel = get(l1Object, 'channel');
    const adsOrderId = get(l1Object, 'adsOrderId');
    // ECOPD-5566：停用渠道底下的群組轉唯讀 —— 仍可查看，但沒有編輯入口。
    // 掛在這裡一次覆蓋群組詳情頁與群組列表的兩個掛載點。
    let basicPermissionAware = hasFuncs(Permission.CAMPAIGN_WRITE).and(
      notArchivedChannel(get(l1Object, 'channelGroupId'))
    );
    if (channel) {
      const channelFeatureName = ADDONFEATURE.CHANNEL[channel];
      if (channelFeatureName) {
        basicPermissionAware = basicPermissionAware.and(
          addOnEnabled(channelFeatureName)
        );
      }
    }

    if (adsOrderId) {
      basicPermissionAware.and(isPmax3Order(adsOrderId));
    }

    return basicPermissionAware;
  }

  isOutdoorChannel(channel: string) {
    const channelSetting = ChannelGroupSetting.fromKey(channel);
    return channelSetting.isOutdoorChannel() ?? false;
  }

  isChannelSupportOutdoorTA(channel: string) {
    const channelGroupSetting = ChannelGroupSetting.fromKey(channel);
    return channelGroupSetting.supportOutdoorTA();
  }

  /**
   * 地圖選門市需要的渠道設定。
   *
   * 品牌名稱與 pin 圖示讀渠道設定裡的 brand，沒填就是空字串：品牌名稱為空時搜尋只用
   * 地址與門市名，圖示為空時地圖畫系統預設的 pin。`language` 是地圖目前的語系
   * （`zh-TW` / `en` / `ja`），只取該語系的品牌名稱，不退到其他語系。
   */
  getDeliveryTargetConfig(
    channelGroupId?: number,
    language?: string
  ): DeliveryTargetConfig {
    if (!channelGroupId) {
      return {
        brandName: '',
        addBtnI18n: 'limitation.buttons.addStore',
        removeBtnI18n: 'limitation.buttons.removeStore',
        filteredIcon: '',
        normalIcon: ''
      };
    }

    const channelGroupSetting = ChannelGroupSetting.fromId(channelGroupId);
    const deliveryUnit = channelGroupSetting.config.deliveryUnit;
    const isDevice = deliveryUnit === DeliveryUnit.DEVICE;
    const addBtnI18n = isDevice
      ? 'limitation.buttons.addDevice'
      : 'limitation.buttons.addStore';
    const removeBtnI18n = isDevice
      ? 'limitation.buttons.removeDevice'
      : 'limitation.buttons.removeStore';

    const brand = channelGroupSetting.config.brand;

    return {
      brandName: getBrandName(brand, language),
      addBtnI18n,
      removeBtnI18n,
      filteredIcon: getBrandIconUrl(brand?.filteredIconUrl),
      normalIcon: getBrandIconUrl(brand?.normalIconUrl)
    };
  }
}
